// @flow
import {connect} from 'react-redux';
import {compose, withProps} from 'react-recompose';

import {addPaymentMeanDuringOrderSim} from '../../../modules/Payment';
import {confirmPlan} from '../../../modules/SignUp/actions';
import {
  customPaymentMeanSelector,
  hasCustomPaymentMeanSelector,
} from '../../../modules/Customer/selectors';
import {deliveryAddressSelector} from '../../../modules/SignUp/DeliveryAddress/reducer';
import {deliveryContactSelector} from '../../../modules/SignUp/DeliveryContact/reducer';
import {deliveryMethodSelector} from '../../../modules/SignUp/DeliveryMethod/selectors';
import {fetchAgreementsByProductOfferings} from '../../../modules/Agreements/actions';
import {hasAlreadyValidatedNeededAgreementsSelector} from '../../../modules/Agreements/selectors';
import {isLoadingSelector} from '../../../modules/LoadingStatus/reducer';
import {newOfferPriceSelector} from '../../../modules/NewOffer/selectors';
import {simProductPriceSelector} from '../../../modules/SignUp/Sim/selectors';
import {swapBackupAndCurrentSubscriptionOuid} from '../../../modules/Dashboard/actions';
import {verifyBonuses} from '../../../modules/SignUp/Bonus/actions';
import {
  chosenPhoneNumberSelector,
  portabilityCharacteristicsSelector,
} from '../../../modules/SignUp/PhoneNumber/reducer';
import {isOrderingReplacementSim as orderingReplacementSim} from '../../../lib/navigation';
import I18n from '../../../lib/i18n';
import theme from '../../../theme-config';

import ConfirmPlan from './ConfirmPlan.container';

import type {AddressType, PaymentMeanType} from 'digicel-kaizen-library';
import type {StateType} from '../../../modules/reducers';
import type {DeliveryContactType} from '../../../modules/SignUp/DeliveryContact/reducer';
import type {DeliveryMethodChoiceType} from '../../../modules/SignUp/DeliveryMethod/reducer';
import type {Props} from './ConfirmPlan';
import {hasRefreshedAtLeastOnceSelector} from '../../../modules/Dashboard';

import type {
  NavigationRoute,
  NavigationScreenProp,
} from '@react-navigation/compat';

import {
  setCurrentVoucherDetails,
  setVoucherDetailsComeFrom,
  voucherCurrentVoucherDetailsSelector,
  voucherFilteredAvailableVouchersSelectorLazy,
} from '../../../modules/Voucher';
import {
  eSimSelectedSelector,
  eSimProductPriceSelector,
} from '../../../modules/ESim/selectors';

import {swapSim} from '../../../modules/SuspendSwapTerminate';

import {fetchFreeTrialActivationBonus} from '../../../modules/Bonuses';
import {freeTrialActivationBonusStateSelector} from '../../../modules/Bonuses';

type StateProps = {
  agreementText: string,
  bringBackTo: string,
  confirmButtonLabel: string,
  deliveryAddress: AddressType,
  deliveryContact: ?DeliveryContactType,
  deliveryMethod: ?DeliveryMethodChoiceType,
  editPlanRoute: string,
  hideCheckboxes: boolean,
  isConfirmPlanLoading: boolean,
  isKeepingNumber: boolean,
  isOrderingReplacementSim: boolean,
  isOrderingReplacementSimImg: number,
  isSignup: boolean,
  isVerifyingBonuses: boolean,
  modalHtmlFile: string,
  paymentMean: PaymentMeanType,
  phoneNumber: ?string,
  price: number,
  simPrice: number | null,
  skipPayment: boolean,
};

type DispatchProps = {
  confirmPlan: Function,
  fetchAgreements: Function,
  mockConfirmPayment: Function,
  swapBackupAndCurrentSubscriptionOuid: Function,
  verifyBonuses: Function,
};

const mapStateToProps = (state: StateType): StateProps => {
  const deliveryMethod = deliveryMethodSelector(state);
  const hasPaymentMean = hasCustomPaymentMeanSelector(state);
  const isOrderingReplacementSim = orderingReplacementSim();
  const eSimSelected = eSimSelectedSelector(state);
  const swapSimOrEsimImage = eSimSelected
    ? theme.eSimSwapImage
    : theme.simReplacementImage;

  return {
    agreementText: I18n.t('SignUp.ConfirmPlan.PDPA'),
    bringBackTo: isOrderingReplacementSim ? 'simReplacement' : 'confirmPlan',
    confirmButtonLabel:
      !!deliveryMethod && deliveryMethod.price === 0
        ? 'confirm'
        : 'confirmAndPay',
    deliveryAddress: deliveryAddressSelector(state),
    deliveryContact: deliveryContactSelector(state),
    deliveryMethod,
    editPlanRoute: 'editPlanDuringSignUp',
    hasRefreshDashboardAtLeastOnce: hasRefreshedAtLeastOnceSelector(state),
    hasPaymentMean: hasCustomPaymentMeanSelector(state),
    hideCheckboxes:
      isOrderingReplacementSim ||
      hasAlreadyValidatedNeededAgreementsSelector(state),
    isConfirmPlanLoading: isLoadingSelector(state, 'confirmPlan'),
    isKeepingNumber: !!portabilityCharacteristicsSelector(state),
    isOrderingReplacementSim,
    isOrderingReplacementSimImg: swapSimOrEsimImage,
    isSignup: !isOrderingReplacementSim,
    isVerifyingBonuses: isLoadingSelector(state, 'verifyBonuses'),
    modalHtmlFile: 'pdpa.html',
    paymentMean: customPaymentMeanSelector(state),
    phoneNumber: chosenPhoneNumberSelector(state),
    price: newOfferPriceSelector(state),
    simPrice: simProductPriceSelector(state),
    skipPayment: hasPaymentMean,
    currentVoucherDetails: voucherCurrentVoucherDetailsSelector(state),
    filteredAvailableVouchers:
      voucherFilteredAvailableVouchersSelectorLazy(state),
    eSimSelected: eSimSelectedSelector(state),
    eSimPrice: eSimProductPriceSelector(state),
    freeTrialActivationBonus: freeTrialActivationBonusStateSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
  swapWithEsim: () => dispatch(swapSim()),
  confirmPlan: isOrderingReplacementSim =>
    dispatch(confirmPlan(isOrderingReplacementSim)),
  mockConfirmPayment: paymentInfo =>
    dispatch(addPaymentMeanDuringOrderSim({paymentInfo})),
  verifyBonuses: () => dispatch(verifyBonuses()),
  fetchAgreements: (isSignup, options) =>
    dispatch(fetchAgreementsByProductOfferings(isSignup, options)),
  swapBackupAndCurrentSubscriptionOuid: () =>
    dispatch(swapBackupAndCurrentSubscriptionOuid()),
  setVoucherDetailsComeFrom: (comeFrom: string) =>
    dispatch(setVoucherDetailsComeFrom(comeFrom)),
  setCurrentVoucherDetails: (object: Object) =>
    setCurrentVoucherDetails(object),
  fetchFreeTrial: () => fetchFreeTrialActivationBonus(),
});

const withActivationProps = (): {confirmPlanType: string} => ({
  confirmPlanType: 'signup',
});

const navigateToPaymentRegistration = (
  navigation: NavigationScreenProp<NavigationRoute>,
  options: Object,
) =>
  navigation.navigate('registerPaymentDetails', {
    redirectTo: 'payment',
    ...options,
  });

const mergeProps = (stateProps, dispatchProps, ownProps): Props => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  goToNewPaymentMeanPage: () =>
    navigateToPaymentRegistration(ownProps.navigation, {
      bringBackTo: stateProps.bringBackTo,
      isPaymentMeanUpdate: true,
    }),
  submit: (isOrderingReplacementSim: boolean) => {
    dispatchProps.confirmPlan(isOrderingReplacementSim);
  },
  swapWithESim: () => {
    dispatchProps.swapWithEsim();
  },
});

export default compose(
  withProps(withActivationProps()),
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
)(ConfirmPlan);
